import React, { useCallback, useReducer, useRef, useState } from 'react';

import { REQUEST_FROM } from 'store/reviewList/types';

import { Divider } from '@travel/design-system/core/src/components/Divider';
import cx from '@travel/icons/_utils/classnames';
import { ArrowUp, Attention } from '@travel/icons/ui';
import { Translate } from '@travel/translation';
import { useDeviceTypes, useDialogHandler, useHasMounted } from '@travel/traveler-core/hooks';
import { FlatButton, IconTextLink } from '@travel/ui';
import LoadingCircle from '@travel/ui/components/FlatButton/loadingCircle';

import MediaExpandedView from 'components/MediaExpandedView/index';
import MultiErrorDisplay from 'components/MultiErrorDisplay';
import MyReviewLoader from 'components/MyReviewLoader';
import { RAT_MY_REVIEW_URL } from 'constants/ratAnalytics';
import { initialState, mediaListReducer, MediaListContext } from 'context/mediaListContext';
import { RatTagProvider } from 'context/ratTagContext';
import useSharedAdobeTag from 'hooks/useSharedAdobeTag';
import useViewCount from 'hooks/useViewCount';
import SNSSection from 'pages/MyReviewEdit/components/SNSSection';
import { getIsLoggedIn } from 'store/authCode/selectors';
import { selectInlineErrorLabel } from 'store/commonError/selectors';
import {
  getIsProfileByIdLoading,
  getIsViewingOtherUser,
  getProfileById,
} from 'store/profileById/selector';
import { fetchReviewList } from 'store/reviewList/actions';
import { getIsFetchingReviewList, getReviewsList } from 'store/reviewList/selector';
import { fetchUserProfile, updateSNSData } from 'store/userProfile/actions';
import { getUserProfile, isUserProfileLoading } from 'store/userProfile/selectors';
import { ACTION_SNS, MY_REVIEW_PAGE } from 'utils/ratHelper';
import { scrollToTop } from 'utils/scrollToTop';
import MyReviewCard from './components/MyReviewCard';
import ProfileDetails from './components/ProfileDetails';

import { REVIEW_LIST_LOAD_MORE_LIMIT } from 'constants/common';
import { ApiErrorCodes, ReviewStatus } from 'store/apiTypes';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import styles from './myReviewPage.module.scss';

import type { UserProfileSns } from 'store/apiTypes';
import type { SortBy } from 'store/reviewList/types';

const MyReviewPage = () => {
  const [state, mediaDispatch] = useReducer(mediaListReducer, {
    ...initialState,
    shouldHideNonOKStatusMedia: true,
  });
  const [isPaginating, setIsPaginating] = useState(false);
  const { isOpen, onOpen, onClose } = useDialogHandler(false);

  const dispatch = useAppDispatch();
  const { isPC } = useDeviceTypes();

  const isLoggedIn = useAppSelector(getIsLoggedIn);
  const isViewingOtherUser = useAppSelector(getIsViewingOtherUser);
  const userReviews = useAppSelector(getReviewsList);
  const isFetching = useAppSelector(getIsFetchingReviewList);
  const isFetchingProfile = useAppSelector(
    isViewingOtherUser ? getIsProfileByIdLoading : isUserProfileLoading,
  );
  const userProfileInfo = useAppSelector(isViewingOtherUser ? getProfileById : getUserProfile);
  const targetRef = useRef<Array<Element | null>>([]);
  const isReviewListEmpty = userProfileInfo.reviewCount === 0;
  const myReviewPageErrors = useAppSelector(selectInlineErrorLabel);

  useSharedAdobeTag(RAT_MY_REVIEW_URL);
  const [isSNSPopupOpen, setIsSNSPopupOpen] = useState(false);

  const handleOpenSNS = () => setIsSNSPopupOpen(true);

  useViewCount({ targetRef, contents: userReviews.contents, isDisabled: !isViewingOtherUser });
  const ratTagValues = {
    action: ACTION_SNS,
    pageName: MY_REVIEW_PAGE,
  };

  const updateSNSLinks = useCallback(
    async (getUpdatedUrls: UserProfileSns[]) => {
      const resultAction = await dispatch(updateSNSData(getUpdatedUrls));

      if (updateSNSData.fulfilled.match(resultAction)) {
        dispatch(fetchUserProfile());
      }
    },
    [dispatch],
  );

  const handleLoadMore = async () => {
    setIsPaginating(true);

    const newOffset = (userReviews.offset || 0) + REVIEW_LIST_LOAD_MORE_LIMIT;

    await dispatch(
      fetchReviewList({
        sortBy: 'MY_REVIEW_PAGE' as SortBy,
        offset: newOffset,
        limit: REVIEW_LIST_LOAD_MORE_LIMIT,
        userId: String(userProfileInfo.userId),
        requestFrom: REQUEST_FROM.MY_REVIEW,
      }),
    );

    setIsPaginating(false);
  };

  const hasMore = userReviews.contents.length < userReviews.total;
  const isUserWithdrawn = myReviewPageErrors?.some(
    (field) => field.labelId === ApiErrorCodes.USER_WITHDRAWN,
  );

  const hasMounted = useHasMounted();

  if (!hasMounted) {
    return (
      <div className={styles.skeletonWrapper}>
        <MyReviewLoader />
      </div>
    );
  }

  // When review list is not empty but contents array is empty, it means
  // the initial fetch hasn't been called yet (data still loading from API)
  const isReviewListDataStillNotFetchedYet =
    !isReviewListEmpty && userReviews.contents.length === 0;

  const shouldShowSkeleton =
    ((isFetching || isFetchingProfile) &&
      !isPaginating &&
      (userReviews.contents.length === 0 || userReviews.offset === 0)) ||
    (!isLoggedIn && !isViewingOtherUser) ||
    isReviewListDataStillNotFetchedYet;

  if (shouldShowSkeleton) {
    return (
      <div className={styles.skeletonWrapper}>
        <MyReviewLoader />
      </div>
    );
  }

  return (
    <MediaListContext.Provider value={{ state, dispatch: mediaDispatch }}>
      <RatTagProvider values={ratTagValues}>
        <div
          className={cx(
            styles.myReviewPageContainer,
            isReviewListEmpty || isUserWithdrawn ? styles.reviewErrorUI : '',
          )}
        >
          {isReviewListEmpty ? (
            <MultiErrorDisplay containerClassName={styles.errorContainer} />
          ) : isUserWithdrawn ? (
            <MultiErrorDisplay
              errorType="userHasWithdrawn"
              containerClassName={styles.errorContainer}
            />
          ) : (
            <>
              <ProfileDetails {...userProfileInfo} handleOpenSNS={handleOpenSNS} />
              <Divider
                variant={isPC ? 'default' : 'card'}
                className={isPC ? styles.defaultDivider : styles.cardDivider}
              />
              {userReviews?.contents?.map((review, index) => (
                <React.Fragment key={review.id}>
                  {(!isViewingOtherUser || !review.isAnonymous) && (
                    <>
                      <div
                        className={styles.reviewContainer}
                        ref={(el) => {
                          targetRef.current[index] = el;
                        }}
                        // data-id is added for useViewCount intersection observer
                        data-id={review.id}
                        data-testid="myReviewPage-reviewList-container"
                      >
                        {review.status === ReviewStatus.HIDDEN && !isViewingOtherUser && (
                          <div
                            className={styles.deleteReason}
                            data-testid="myReviewPage-hideReason"
                          >
                            <Attention size={16} className={styles.deleteReasonIcon} />
                            <Translate
                              id="MyReview.PostBannedNotify"
                              data={{ delete_reason: review.hideReason }}
                            />
                          </div>
                        )}

                        <MyReviewCard
                          reviewData={review}
                          onClickMedia={onOpen}
                          userProfileInfo={userProfileInfo}
                        />
                      </div>
                      {(index != userReviews.contents.length - 1 || hasMore) && (
                        <Divider
                          variant={isPC ? 'default' : 'card'}
                          className={isPC ? styles.defaultDivider : styles.cardDivider}
                        />
                      )}
                    </>
                  )}
                </React.Fragment>
              ))}
              {hasMore && (
                <div className={styles.LoadMoreContainer}>
                  {isFetching ? (
                    <div className={styles.spinnerWrapper} data-testid="reviewPage-loading-spinner">
                      <LoadingCircle classType="primary" />
                    </div>
                  ) : (
                    <FlatButton
                      onClick={handleLoadMore}
                      classType="secondary"
                      className={styles.loadMoreButton}
                      data-testid="reviewPage-loadMore-button"
                    >
                      <Translate id="Review_Common.LoadMore" />
                    </FlatButton>
                  )}
                </div>
              )}
              <div className={styles.gotToTopContainer}>
                <IconTextLink
                  onClick={scrollToTop}
                  className={styles.gotToTopLabel}
                  icon={<ArrowUp size={16} />}
                  iconPosition="left"
                  text={<Translate id="Review.Web.BackToTop" />}
                  data-testid="reviewPage-gotToTop-button"
                  unhoveredColor={'currentColor'}
                  hoveredColor={'currentColor'}
                />
              </div>
            </>
          )}
        </div>

        <MediaExpandedView
          isOpen={isOpen}
          onClose={onClose}
          reviewIndex={state.mediaIndex}
          reviewMediaList={state.galleryMediaList}
        />
      </RatTagProvider>
      <SNSSection
        isSNSPopupOpen={isSNSPopupOpen}
        setIsSNSPopupOpen={setIsSNSPopupOpen}
        onSubmitSNSLinks={updateSNSLinks}
      />
    </MediaListContext.Provider>
  );
};

export default MyReviewPage;
